import { executeCapability } from '../utils/capabilityExecute'

export type AuditLogRow = {
  _id: string
  ts: string
  requestId?: string
  userId?: string | null
  employeeId?: string | null
  accessLevel?: string | null
  apiKeyId?: string | null
  apiKeyName?: string | null
  apiKeyPrefix?: string | null
  app: string
  capability: string
  statusCode: number
  success: boolean
  durationMs: number
  ip?: string
  userAgent?: string
  inputRedacted?: any
  inputKeys?: string[]
  inputBytesApprox?: number
  error?: { message?: string; name?: string; stack?: string; status?: number } | null
}

export type AuditLogQuery = {
  limit?: number
  fromTs?: string
  toTs?: string
  userId?: string
  employeeId?: string
  capability?: string
  app?: string
  success?: boolean
}

export type FormCompletionRow = {
  _id: string
  ts: string
  requestId?: string | null
  userId?: string | null
  employeeId?: string | null
  username?: string | null
  accessLevel?: string | null
  ip?: string | null
  userAgent?: string | null
  formName: string
  formVersion?: unknown
  workflow?: string | null
  fileName?: string | null
  pdfSha256: string
  pdfBytes: number
  destinations?: unknown
  uploadSucceeded?: boolean | null
}

export type FormCompletionQuery = {
  limit?: number
  fromTs?: string
  toTs?: string
  userId?: string
  employeeId?: string
  formName?: string
}

export type FormCompletionPdf = {
  id: string
  fileName: string
  pdfBase64: string
  pdfSha256: string
}

function pdfBase64ToBlobUrl(pdfBase64: string): string {
  const binary = atob(pdfBase64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
}

export const auditService = {
  async listCapabilityExecLogs(query: AuditLogQuery): Promise<AuditLogRow[]> {
    const out = await executeCapability<{ data: AuditLogRow[] }>({
      app: 'admin',
      capability: 'audit.capabilities.list',
      input: query,
    })
    return out?.data || []
  },

  async listFormCompletions(query: FormCompletionQuery): Promise<FormCompletionRow[]> {
    const out = await executeCapability<{ data: FormCompletionRow[] }>({
      app: 'admin',
      capability: 'audit.formCompletions.list',
      input: query,
    })
    return out?.data || []
  },

  async getFormCompletionPdf(id: string): Promise<FormCompletionPdf> {
    return executeCapability<FormCompletionPdf>({
      app: 'admin',
      capability: 'audit.formCompletions.getPdf',
      input: { id },
    })
  },

  async getFormCompletionPdfBlobUrl(id: string): Promise<{ blobUrl: string; fileName: string }> {
    const pdf = await this.getFormCompletionPdf(id)
    return {
      blobUrl: pdfBase64ToBlobUrl(pdf.pdfBase64),
      fileName: pdf.fileName || 'form.pdf',
    }
  },
}
